import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import HeroWordmark from "@/components/landing/HeroWordmark";
import MaskedLines from "@/components/landing/MaskedLines";
import Magnetic from "@/components/landing/Magnetic";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_3d06aecc-7c40-4cf7-8c1b-6c21e0f317e1/artifacts/8fw04mqk_Blue_Blue_Blue.png";
const LOGO =
  "https://customer-assets.emergentagent.com/job_scroll-elegance-1/artifacts/3iupz0o1_logoHB_bgremoved.png";

export default function Hero() {
  const ref = useRef(null);
  const [veilDone, setVeilDone] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgScale = useTransform(scrollYProgress, [0, 1], [1.12, 1.32]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.95]);
  // Tagline + CTAs drift up and fade as the letters fly away
  const tailY = useTransform(scrollYProgress, [0, 1], ["0%", "-60%"]);
  const tailOpacity = useTransform(scrollYProgress, [0, 0.45], [1, 0]);

  // Mouse-tracked parallax for the mist
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 40, damping: 18, mass: 0.6 });
  const smoothY = useSpring(mouseY, { stiffness: 40, damping: 18, mass: 0.6 });

  // Base layer drifts opposite to cursor (subtle)
  const baseX = useTransform(smoothX, (v) => v * -12);
  const baseY = useTransform(smoothY, (v) => v * -8);
  // Mist layer drifts WITH cursor a bit more (creates depth)
  const mistX = useTransform(smoothX, (v) => v * 22);
  const mistY = useTransform(smoothY, (v) => v * 14);
  // Deepest mist layer
  const mist2X = useTransform(smoothX, (v) => v * 36);
  const mist2Y = useTransform(smoothY, (v) => v * 22);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5; // -0.5 .. 0.5
      const y = (e.clientY - r.top) / r.height - 0.5;
      mouseX.set(x);
      mouseY.set(y);
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, [mouseX, mouseY]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative h-[120vh] w-full overflow-hidden grain"
    >
      {/* Signature on-load moment — midnight veil lifts to reveal the scene */}
      {!veilDone && (
        <motion.div
          data-testid="intro-veil"
          initial={{ y: 0 }}
          animate={{ y: "-100%" }}
          transition={{ duration: 1.05, delay: 1.5, ease: [0.77, 0, 0.18, 1] }}
          onAnimationComplete={() => setVeilDone(true)}
          className="fixed inset-0 z-[80] bg-[#060912] flex flex-col items-center justify-center pointer-events-none"
          aria-hidden="true"
        >
        <motion.img
          src={LOGO}
          alt=""
          initial={{ opacity: 0, scale: 0.9, filter: "blur(8px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="h-20 md:h-24 w-auto drop-shadow-[0_2px_24px_rgba(212,175,55,0.4)]"
        />
        <div className="mt-10 h-px w-44 bg-white/10 overflow-hidden">
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.05, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="h-full w-full origin-left bg-[#D4AF37]"
          />
        </div>
        </motion.div>
      )}

      {/* Base background image with parallax + cursor drift */}
      <motion.div
        style={{ scale: imgScale, y: imgY, x: baseX }}
        className="absolute inset-0 z-0"
      >
        <motion.img
          src={HERO_IMG}
          alt="HueBleu — Blue Mountains"
          style={{ y: baseY }}
          className="w-full h-full object-cover drift-slow"
        />
      </motion.div>

      {/* Live mist layer — same image, drifting + breathing colors, blended */}
      <motion.div
        aria-hidden
        style={{ x: mistX, y: mistY }}
        className="absolute inset-0 z-[1] pointer-events-none mix-blend-screen opacity-70"
      >
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover mist-drift hue-breath"
        />
      </motion.div>

      {/* Second mist layer — slower, different hue cycle, lighter */}
      <motion.div
        aria-hidden
        style={{ x: mist2X, y: mist2Y }}
        className="absolute inset-0 z-[2] pointer-events-none mix-blend-lighten opacity-30"
      >
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover mist-drift hue-breath"
          style={{ animationDuration: "26s, 17s", animationDirection: "reverse" }}
        />
      </motion.div>

      {/* Dark overlays for legibility */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-10 bg-gradient-to-b from-[#0a0f1d]/30 via-[#0a0f1d]/40 to-[#0a0f1d]"
      />
      <div className="absolute inset-0 z-10 bg-[radial-gradient(ellipse_at_center,_transparent_30%,_#0a0f1d_85%)]" />

      {/* Mask the baked-in wordmark in the source image with a feathered horizontal band */}
      <div
        className="absolute inset-x-0 z-10 pointer-events-none"
        style={{
          top: "28%",
          height: "44%",
          background:
            "linear-gradient(to bottom, rgba(10,15,29,0) 0%, rgba(10,15,29,1) 22%, rgba(10,15,29,1) 78%, rgba(10,15,29,0) 100%)",
        }}
      />

      {/* Content */}
      <div
        className="relative z-20 h-screen flex flex-col items-center justify-center px-6 text-center"
        style={{ isolation: "isolate" }}
      >
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 2.0 }}
          className="label-overline mb-8"
          data-testid="hero-overline"
        >
          Blue Mountains · New South Wales · Australia
        </motion.span>

        <HeroWordmark scrollTargetRef={ref} />

        <motion.div
          style={{ y: tailY, opacity: tailOpacity }}
          className="flex flex-col items-center"
        >
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 1.4, delay: 2.7 }}
            className="my-10 h-px w-44 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"
          />

          <MaskedLines
            as="p"
            data-testid="hero-tagline"
            animateOn="mount"
            delay={2.9}
            stagger={0.14}
            className="font-serif-display italic text-white/85 text-xl md:text-2xl max-w-2xl leading-relaxed"
            lines={[
              "Heavy cotton, soft as the morning mist.",
              "Cut, sewn, and dyed in the Blue Mountains.",
            ]}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 3.3 }}
            className="mt-12 flex flex-col sm:flex-row gap-4 items-center"
          >
            <Magnetic strength={0.3}>
              <a
                href="#gallery"
                data-testid="hero-cta-primary"
                className="group relative inline-flex items-center gap-3 px-9 py-4 border border-[#D4AF37] text-[#D4AF37] tracking-[0.25em] text-xs uppercase overflow-hidden transition-colors duration-500 hover:text-[#0a0f1d]"
              >
                <span className="absolute inset-0 bg-[#D4AF37] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-[cubic-bezier(0.77,0,0.18,1)]" />
                <span className="relative z-10">Explore the Collection</span>
              </a>
            </Magnetic>
            <a
              href="#about"
              data-testid="hero-cta-secondary"
              className="gold-link text-xs tracking-[0.25em] uppercase text-white/70 ml-0 sm:ml-2"
            >
              Our Story →
            </a>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.6, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase text-white/50">
          Scroll
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-[#D4AF37]"
        >
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
}
